import React, { FC, useRef, RefObject } from 'react';
import classNames from 'classnames';
import styles from './search_bar_field.module.scss';

type Props = {
  isOpen: boolean;
  forwardRef: RefObject<HTMLInputElement>;
  query: string;
  placeholder: string;
  onChange: (data: {
    newQuery: string;
    queryBeforePasting: string;
    isPasted: boolean;
  }) => void;
  onClose: Function;
  onClear: Function;
  onSubmit: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  onFocus: (event: React.FocusEvent<HTMLInputElement>) => void;
};

export const SearchBarField: FC<Props> = ({
  query,
  isOpen,
  onFocus,
  onSubmit,
  placeholder,
  onClear,
  onClose,
  forwardRef,
  onChange,
}) => {
  const beforePasting = useRef('');
  const pasteFlag = useRef(false);

  const onInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.currentTarget;

    onChange({
      newQuery: value,
      isPasted: pasteFlag.current,
      queryBeforePasting: beforePasting.current,
    });

    if (pasteFlag.current) {
      pasteFlag.current = false;
      beforePasting.current = '';
    }
  };

  const onMouseDown = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    onClose();

    if (forwardRef.current) {
      forwardRef.current.blur();
    }
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>): void => {
    // if we paste a new query that replace totally the old one we reset the cats
    beforePasting.current = event.currentTarget.value;
    pasteFlag.current = true;
  };

  const clearClicked = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    event.preventDefault();
    onClear();
    if (forwardRef.current) {
      forwardRef.current.focus();
    }
  };

  const hasQuery = query.length > 0;

  return (
    <label className={styles.label}>
      <button
        aria-label="Close search bar"
        type="button"
        onMouseDown={onMouseDown}
        className={classNames(styles.close, !isOpen && styles.hideClose)}
      />
      <form action="" onSubmit={(evt) => evt.preventDefault()}>
        <input
          ref={forwardRef}
          data-test-id="search-input"
          name="search-input"
          data-tour="search-advice"
          className={classNames(
            styles.input,
            isOpen && styles.focused,
            hasQuery && styles.actions,
          )}
          id="search_input"
          type="text"
          autoComplete="off"
          spellCheck={false}
          placeholder={placeholder}
          value={query}
          onChange={onInputChange}
          onPaste={handlePaste}
          onFocus={onFocus}
          maxLength={1000}
        />
      </form>
      <>
        {hasQuery && (
          <button
            aria-label="Clear search bar"
            type="button"
            onClick={clearClicked}
            className={styles.clear}
          />
        )}
        <button
          aria-label="Submit search"
          type="button"
          disabled={query.length === 0}
          onClick={onSubmit}
          className={styles.submit}
        />
      </>
    </label>
  );
};
